import { cn } from "@/libs/cn";
import { GridTableCell, Tag, Tooltip } from "@/modules/common/components";
import { ADMIN_USER_ROLE_OPTIONS, ADMIN_USER_STATUS_OPTIONS, AdminUserRole, AdminUserStatus } from "@/modules/common/types/Users.enum";
import { formatDate, formatNameInitials, formatValue } from "@/modules/common/utils/format";
import { AdminUserResponse } from "@/service/response/admin-users";

import { gridLayout } from "./UserListHeader";
import { generatePath, Link } from "react-router-dom";
import { ROUTES } from "@/routes/constants/routes";

interface UserListLineProps {
  user: AdminUserResponse;
}

const UserListLine = ({ user }: UserListLineProps) => {
  const isActive = user.status === AdminUserStatus.ACTIVE;
  const isAdmin = user.role === AdminUserRole.ADMIN;
  const statusLabel = ADMIN_USER_STATUS_OPTIONS.find((option) => option.value === String(user.status))?.label ?? "-";
  const roleLabel = ADMIN_USER_ROLE_OPTIONS.find((option) => option.value === String(user.role))?.label ?? "-";

  const rowClassName = cn(
    gridLayout,
    "bg-white border border-[#D6DEE9] md:rounded-none md:border-0 md:border-t md:bg-transparent rounded-lg",
    user.id && "hover:bg-ui-hover transition-colors",
  );

  const cells = (
    <>
      <GridTableCell label="Nome" className="md:min-w-0">
        <div className="flex items-center gap-3 md:min-w-0">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-800/10 text-sm font-semibold text-blue-800">
            {formatNameInitials(user.name)}
          </div>
          <div className="flex min-w-0 flex-col">
            <Tooltip label={user.name}>
              <span className="block truncate text-sm font-medium text-ui-navy-dark">{user.name}</span>
            </Tooltip>
            <span className="block truncate text-xs text-ui-text-muted">
              {user.phoneNumber ? formatValue("phoneNumber", user.phoneNumber) : "-"}
            </span>
          </div>
        </div>
      </GridTableCell>

      <GridTableCell label="E-mail" className="text-sm text-ui-navy-dark md:min-w-0">
        <Tooltip label={user.email}>
          <span className="block truncate">{user.email}</span>
        </Tooltip>
      </GridTableCell>

      <GridTableCell label="Status">
        <Tag label={statusLabel} variant={isActive ? "success" : "neutral"} />
      </GridTableCell>

      <GridTableCell label="Perfil" className="text-sm text-ui-navy-dark md:min-w-0">
        <Tag label={roleLabel} variant={isAdmin ? "primary" : "neutral"} />
      </GridTableCell>

      <GridTableCell label="Último acesso" className="text-sm text-ui-navy-dark">
        {user.lastLoginAt ? formatDate(user.lastLoginAt, "DD/MM/YYYY [às] HH:mm") : "-"}
      </GridTableCell>

      <GridTableCell label="Editado por" className="text-sm text-ui-navy-dark md:min-w-0">
        <Tooltip label={user.editedBy?.name} disabled={!user.editedBy?.name}>
          <span className="block truncate">{user.editedBy?.name ?? "-"}</span>
        </Tooltip>
      </GridTableCell>

      <GridTableCell label="Criado em" className="text-sm text-ui-navy-dark">
        {formatDate(user.createdAt, "DD/MM/YYYY")}
      </GridTableCell>

      <GridTableCell label="Atualizado em" className="text-sm text-ui-navy-dark">
        {formatDate(user.updatedAt, "DD/MM/YYYY")}
      </GridTableCell>
    </>
  );

  if (!user.id) {
    return <div className={rowClassName}>{cells}</div>;
  }

  return (
    <Link to={generatePath(ROUTES.USERS.DETAILS, { id: user.id })} className={rowClassName}>
      {cells}
    </Link>
  );
};

export default UserListLine;
